import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { makeTextFieldError, makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { Error, type IErrorProps, SpinnerLoader } from 'shared/ui'
import { truthy } from 'shared/lib'
import { type AgentDocumentsStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentDocumentsStore'
import { DocumentsSelectTrigger } from './DocumentsSelectTrigger'
import styles from './styles.module.scss'

export type IKnowledgeBaseDocumentsSelectProps = {
  error?: { message: string; resetError?: () => void }
  agentDocumentsStore: AgentDocumentsStore
  knowledgeBaseDocuments: number[]
  setKnowledgeBaseDocuments: (value: number[]) => void
  errorType?: IErrorProps['type']
}

export const KnowledgeBaseDocumentsSelect = observer(
  ({
    error,
    agentDocumentsStore,
    knowledgeBaseDocuments,
    setKnowledgeBaseDocuments,
    errorType = 'tooltip',
  }: IKnowledgeBaseDocumentsSelectProps) => {
    const {
      init,
      dropdownItems,
      handleSelectChange,
      selectedDocumentIds,
      selectedDocuments,
      isDocumentsLoading,
      isSelectAllDocuments,
      validateDocumentsList,
      documentsError,
    } = agentDocumentsStore

    useEffect(() => {
      init(knowledgeBaseDocuments)
    }, [])

    const errorMessage = error?.message || documentsError

    if (isDocumentsLoading) {
      return <SpinnerLoader ariaLabel={'KnowledgeBaseDocumentsSelect'} />
    }

    return (
      <div className={styles.wrapSelect}>
        <Select
          text=''
          type='multi'
          items={dropdownItems}
          dropdownProps={{
            ariaLabel: 'Select documents',
            textFieldProps: {
              InputProps: { placeholder: 'Search' },
            },
            withSearch: true,
            width: 466,
            maxHeight: 230,
            onChange: handleSelectChange,
            onClose: () => {
              validateDocumentsList(error?.resetError)
              setKnowledgeBaseDocuments(isSelectAllDocuments ? [] : selectedDocumentIds)
            },
            triggerComponent: () => (
              <TextFieldPlaceholder
                variant={'stroke'}
                multiline
                className={styles.input}
                placeholder={
                  <DocumentsSelectTrigger
                    selectedDocuments={selectedDocuments}
                    isSelectAllDocuments={isSelectAllDocuments}
                    isError={!!errorMessage}
                  />
                }
                error={errorMessage}
                rightActions={[
                  makeTextFieldIcon({ icon: 'chevronDown' }),
                  errorType === 'tooltip' &&
                    makeTextFieldError({
                      placement: 'right',
                    }),
                ].filter(truthy)}
              />
            ),
            PopperProps: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    offset: ({ reference }) => {
                      return [-4, -reference.height - 4]
                    },
                  },
                },
              ],
            },
          }}
        />
        {errorType === 'text' && <Error type={'text'} error={errorMessage} />}
      </div>
    )
  }
)
